import React, { useEffect } from "react";
import { useInput } from "react-admin";
import {
  Typography,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import { TAG_KEY_OPTIONS, apiTagsToRows, createEmptyTagRow, getTagApiUrl } from "./util";
import TagValueSelect from "./TagValueSelect";

const useStyles = makeStyles(() => ({
  wrapper: {
    width: "100%",
  },
  row: {
    marginBottom: 8,
  },
  addButton: {
    marginTop: 4,
  },
  error: {
    color: "#d32f2f",
    marginTop: 4,
  },
}));

const TagsInput = ({ source = "tags", label = "Tags", ...props }) => {
  const classes = useStyles();
  const {
    input: { value, onChange },
    meta: { touched, error },
  } = useInput({
    source,
    format: apiTagsToRows,
    parse: apiTagsToRows,
    ...props,
  });

  // Form state is initialized from the API shape ({ ring_material: "14KY" }).
  // Normalize once on load so validation and save use row format consistently.
  useEffect(() => {
    if (!Array.isArray(value) || value.length === 0) return;

    const needsNormalization = value.some(
      tag => !tag || typeof tag !== "object" || !("key" in tag && "value" in tag)
    );

    if (needsNormalization) {
      onChange(apiTagsToRows(value));
    }
  }, [value, onChange]);

  const rows =
    Array.isArray(value) && value.length > 0 ? value : [createEmptyTagRow()];

  const updateRows = newRows => onChange(newRows);

  const handleKeyChange = (index, key) => {
    const next = rows.map((row, rowIndex) =>
      rowIndex === index ? { key, value: "" } : row
    );
    updateRows(next);
  };

  const handleValueChange = (index, tagValue) => {
    const next = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, value: tagValue } : row
    );
    updateRows(next);
  };

  const handleAdd = () => updateRows([...rows, createEmptyTagRow()]);

  const handleRemove = index => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    updateRows(next.length > 0 ? next : [createEmptyTagRow()]);
  };

  return (
    <div className={classes.wrapper}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {rows.map((row, index) => (
        <Grid
          container
          spacing={1}
          alignItems="center"
          key={`tag-row-${index}`}
          className={classes.row}
        >
          <Grid item xs={12} sm={5}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Key</InputLabel>
              <Select
                value={row.key || ""}
                onChange={event => handleKeyChange(index, event.target.value)}
                label="Key"
              >
                <MenuItem value="">
                  <em>Select key</em>
                </MenuItem>
                {TAG_KEY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={10} sm={6}>
            <TagValueSelect
              resource={getTagApiUrl(row.key)}
              value={row.value || ""}
              onChange={tagValue => handleValueChange(index, tagValue)}
            />
          </Grid>
          <Grid item xs={2} sm={1}>
            <IconButton
              aria-label="Remove tag"
              onClick={() => handleRemove(index)}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Box className={classes.addButton} onClick={handleAdd} sx={{ cursor: 'pointer' }}>
        <IconButton aria-label="Add tag" size="small" color="primary">
          <AddIcon />
        </IconButton>
        <Typography variant="caption" component="span">
          Add tag
        </Typography>
      </Box>

      {touched && error && (
        <Typography variant="body2" className={classes.error}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default TagsInput;
